import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkle, Loader2, PartyPopper, Gift, Landmark } from "lucide-react";
import ViralSnapshot from "@/components/ViralSnapshot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFETTI = ["#c9a24a", "#8a6cae", "#5a8c72", "#d4af37", "#b06a8e"];

const RoyalToast = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [popped, setPopped] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [onboarding, setOnboarding] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/toast/${token}`)
      .then((r) => { setData(r.data); setClaimed(r.data.claimed); })
      .catch((e) => setError(e?.response?.data?.detail || "This Royal Gift link is invalid."));
    // If returning from Stripe onboarding, refresh payout status.
    axios.get(`${API}/toast/${token}/payout/status`).then((r) => setPayoutStatus(r.data)).catch(() => {});
  }, [token]);

  const startOnboarding = async () => {
    setOnboarding(true);
    try {
      const r = await axios.post(`${API}/toast/${token}/payout/onboard`, { email });
      if (r.data.onboarding_url) window.location.href = r.data.onboarding_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start payout setup.");
    } finally { setOnboarding(false); }
  };

  const pop = async () => {
    setPopped(true);
    if (navigator.vibrate) navigator.vibrate([30, 50, 30, 50, 80]); // celebration pulse
    if (data?.cash_gift && !claimed) {
      try {
        const r = await axios.post(`${API}/toast/${token}/claim`, {});
        setClaimed(true); setClaimResult(r.data);
      } catch (e) { console.error("Royal Toast claim failed:", e); }
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-red-500 p-6" data-testid="toast-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading your surprise…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" data-testid="toast-page">
      {popped && Array.from({ length: 40 }).map((_, i) => (
        <span key={i} className="confetti" style={{
          left: `${Math.random() * 100}%`,
          background: CONFETTI[i % CONFETTI.length],
          animationDuration: `${1.5 + Math.random() * 1.8}s`,
          animationDelay: `${Math.random() * 0.4}s`,
        }} />
      ))}

      <div className="w-full max-w-md surface rounded-[2rem] p-8 text-center relative z-10 animate-fade-up">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Sparkle className="w-5 h-5 text-white" /></div>
          <span className="gifted-brand text-2xl">GIFTED</span>
        </div>

        {!popped ? (
          <>
            <p className="text-muted-foreground mb-1">You&apos;ve received a</p>
            <h1 className="font-serif text-4xl mb-2">Royal Gift</h1>
            <p className="text-muted-foreground mb-6">for your {data.occasion}, {data.loved_one_name}</p>
            <div className="text-7xl mb-6 select-none">🍾</div>
            <button data-testid="toast-pop-btn" onClick={pop}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-4 text-lg hover:opacity-90 flex items-center justify-center gap-2">
              <PartyPopper className="w-5 h-5" /> Pop the Champagne
            </button>
          </>
        ) : (
          <div data-testid="toast-revealed">
            <div className="text-7xl mb-4 cork-pop select-none">🍾</div>
            {data.image_base64 && (
              <div className="rounded-2xl overflow-hidden border border-border mb-4">
                <img src={`data:image/png;base64,${data.image_base64}`} alt="" className="w-full h-44 object-cover" />
              </div>
            )}
            {data.cash_gift && (
              <div className="cash-reveal mb-5" data-testid="toast-cash">
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1"><Gift className="w-4 h-4 gold-text" /> A cash gift of</div>
                <div className="font-serif text-5xl gold-text mt-1">${Number(data.cash_gift.amount).toFixed(2)}</div>
                {/* Real payout onboarding (Stripe Connect) */}
                {claimResult?.payout?.transferred ? (
                  <div className="text-sm text-secondary mt-2">Sent to your bank 🎉</div>
                ) : payoutStatus?.payouts_enabled ? (
                  <div className="text-xs text-secondary mt-2">Payout account ready — your funds are on the way.</div>
                ) : (
                  <div className="mt-4 surface rounded-2xl p-4 text-left" data-testid="payout-onboard">
                    <div className="text-sm font-medium flex items-center gap-1.5 mb-2"><Landmark className="w-4 h-4 text-primary" /> Receive it in your bank</div>
                    <input data-testid="payout-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                      className="w-full bg-input border border-border rounded-xl px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="your@email.com" />
                    <button data-testid="payout-onboard-btn" onClick={startOnboarding} disabled={onboarding}
                      className="w-full bg-gradient-to-r from-secondary to-primary text-white rounded-xl py-2.5 text-sm hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
                      {onboarding ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening secure setup…</> : "Set up secure payout"}
                    </button>
                    <p className="text-[11px] text-muted-foreground/70 mt-2">Powered by Stripe. Until setup is complete, your gift is held safely in escrow.</p>
                  </div>
                )}
              </div>
            )}
            {data.charity_gift && (
              <div className="cash-reveal mb-5" data-testid="toast-charity">
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1"><Gift className="w-4 h-4 gold-text" /> A donation in your honor to</div>
                <div className="font-serif text-3xl mt-1" style={{ color: "hsl(var(--secondary))" }}>{data.charity_gift.name}</div>
                <div className="font-serif text-4xl gold-text mt-1">${Number(data.charity_gift.amount).toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-2">
                  501(c)(3) · EIN {data.charity_gift.ein} · tax-deductible
                </div>
                <div className="text-[11px] text-muted-foreground/70 mt-1">501(c)(3) impact receipt generated</div>
              </div>
            )}
            {data.message && <p className="font-serif italic text-lg mb-5">&ldquo;{data.message}&rdquo;</p>}

            {data.signatures?.length > 0 && (
              <div data-testid="toast-vault">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Signed with love by</div>
                <div className="grid grid-cols-2 gap-3">
                  {data.signatures.map((sig, i) => (
                    <div key={`${sig.name}-${i}`} className="surface rounded-2xl p-3">
                      {sig.image_base64 && <img src={`data:image/png;base64,${sig.image_base64}`} alt="" className="h-10 mx-auto object-contain" />}
                      <div className="font-medium text-sm mt-1">{sig.name}</div>
                      {sig.note && <div className="text-xs text-muted-foreground italic">&ldquo;{sig.note}&rdquo;</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ViralSnapshot
              occasion={data.occasion}
              cashAmount={data.cash_gift ? data.cash_gift.amount : null}
              charityName={data.charity_gift ? data.charity_gift.name : null}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RoyalToast;
